import { Container, Graphics, Sprite } from 'pixi.js';
import { COLORS } from '../constants/theme';

interface Particle {
  graphics: Graphics;
  angle: number;
  radius: number;
  baseRadius: number;
  orbitSpeed: number;
  flickerPhase: number;
  flickerSpeed: number;
  size: number;
  layer: 'inner' | 'outer';
}

interface ElectricAuraOptions {
  sprite: Sprite;
  size: number;
  color?: number;
  particleCount?: number;
}

/**
 * Electric/glittery circular aura effect with animated sparkles and arcs
 */
export class ElectricAura extends Container {
  private particles: Particle[] = [];
  private arcs: Graphics[] = [];
  private glowRing: Graphics;
  private options: ElectricAuraOptions;
  private animationFrame: number = 0;
  private isAnimating = false;
  private rafId: number | null = null;
  private baseRadius: number;

  constructor(options: ElectricAuraOptions) {
    super();
    this.options = {
      color: COLORS.DANGER,
      particleCount: 28,
      ...options,
    };

    this.baseRadius = options.size / 2 + 4;

    // Create glow ring (drawn behind particles)
    this.glowRing = new Graphics();
    this.addChild(this.glowRing);

    this.createParticles();
    this.createArcs();
    this.drawGlowRing();
  }

  /**
   * Draw the pulsing glow ring
   */
  private drawGlowRing(): void {
    const { color } = this.options;
    this.glowRing.clear();

    // Multiple rings for glow effect
    const layers = 5;
    for (let i = layers - 1; i >= 0; i--) {
      const t = i / (layers - 1);
      const alpha = 0.15 * (1 - t);
      const width = 2 + t * 8;
      this.glowRing.circle(0, 0, this.baseRadius);
      this.glowRing.stroke({ color: color!, width, alpha });
    }
  }

  /**
   * Create sparkle particles in circular arrangement
   */
  private createParticles(): void {
    const { particleCount, color } = this.options;

    // Create particles in two layers: inner (bright, fast) and outer (dimmer, slower)
    const innerCount = Math.floor(particleCount! * 0.4);
    const outerCount = particleCount! - innerCount;

    // Inner layer particles
    for (let i = 0; i < innerCount; i++) {
      const angle = (i / innerCount) * Math.PI * 2 + Math.random() * 0.3;
      this.createParticle(angle, this.baseRadius - 2, 'inner', color!);
    }

    // Outer layer particles
    for (let i = 0; i < outerCount; i++) {
      const angle = (i / outerCount) * Math.PI * 2 + Math.random() * 0.3;
      this.createParticle(angle, this.baseRadius + 3, 'outer', color!);
    }
  }

  private createParticle(
    angle: number,
    radius: number,
    layer: 'inner' | 'outer',
    color: number
  ): void {
    const graphics = new Graphics();
    const size = layer === 'inner' ? 2 + Math.random() * 2 : 1.5 + Math.random() * 1.5;

    this.drawSparkle(graphics, layer === 'inner' ? 0xffffff : color, size);

    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    graphics.x = x;
    graphics.y = y;

    const particle: Particle = {
      graphics,
      angle,
      radius,
      baseRadius: radius,
      orbitSpeed: (layer === 'inner' ? 0.02 : 0.012) * (0.8 + Math.random() * 0.4),
      flickerPhase: Math.random() * Math.PI * 2,
      flickerSpeed: 4 + Math.random() * 6,
      size,
      layer,
    };

    this.particles.push(particle);
    this.addChild(graphics);
  }

  /**
   * Draw a sparkle/star shape
   */
  private drawSparkle(graphics: Graphics, color: number, size: number): void {
    // 4-pointed star sparkle
    graphics.moveTo(0, -size);
    graphics.lineTo(size * 0.25, -size * 0.25);
    graphics.lineTo(size, 0);
    graphics.lineTo(size * 0.25, size * 0.25);
    graphics.lineTo(0, size);
    graphics.lineTo(-size * 0.25, size * 0.25);
    graphics.lineTo(-size, 0);
    graphics.lineTo(-size * 0.25, -size * 0.25);
    graphics.closePath();
    graphics.fill({ color });
  }

  /**
   * Create electric arc graphics
   */
  private createArcs(): void {
    // More arcs for more electric feel
    for (let i = 0; i < 6; i++) {
      const arc = new Graphics();
      arc.visible = false;
      this.arcs.push(arc);
      this.addChild(arc);
    }
  }

  /**
   * Start the animation
   */
  startAnimation(): void {
    if (this.isAnimating) return;
    this.isAnimating = true;

    const animate = () => {
      if (!this.isAnimating) return;
      this.update();
      this.rafId = requestAnimationFrame(animate);
    };
    this.rafId = requestAnimationFrame(animate);
  }

  /**
   * Stop the animation
   */
  stopAnimation(): void {
    if (!this.isAnimating) return;
    this.isAnimating = false;

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  /**
   * Update animation frame
   */
  private update(): void {
    this.animationFrame++;
    const time = this.animationFrame * 0.016; // Roughly 60fps timing

    // Pulse the glow ring
    const glowPulse = 0.7 + Math.sin(time * 3) * 0.3;
    this.glowRing.alpha = glowPulse;
    const glowScale = 0.98 + Math.sin(time * 2) * 0.04;
    this.glowRing.scale.set(glowScale);

    // Update particles
    this.particles.forEach((particle) => {
      // Orbit around the circle
      particle.angle += particle.orbitSpeed;

      // Radius wobble
      const radiusWobble = Math.sin(time * 3 + particle.flickerPhase) * 2;
      particle.radius = particle.baseRadius + radiusWobble;

      // Update position
      particle.graphics.x = Math.cos(particle.angle) * particle.radius;
      particle.graphics.y = Math.sin(particle.angle) * particle.radius;

      // Flickering alpha (electric spark effect)
      const flicker = Math.sin(time * particle.flickerSpeed + particle.flickerPhase);
      const randomFlicker = Math.random() > 0.9 ? 0.2 : 1; // Occasional bright flash or dim
      const baseAlpha = particle.layer === 'inner' ? 0.8 : 0.5;
      particle.graphics.alpha = (baseAlpha + flicker * 0.2) * randomFlicker;

      // Random bright flash
      if (Math.random() > 0.98) {
        particle.graphics.alpha = 1;
      }

      // Scale pulsing
      const scale = 0.6 + Math.sin(time * 4 + particle.flickerPhase) * 0.4;
      particle.graphics.scale.set(scale);

      // Slight jitter for electric feel
      const jitter = particle.layer === 'inner' ? 1.5 : 1;
      particle.graphics.x += (Math.random() - 0.5) * jitter;
      particle.graphics.y += (Math.random() - 0.5) * jitter;
    });

    // Update electric arcs more frequently
    if (this.animationFrame % 3 === 0) {
      this.updateArcs();
    }
  }

  /**
   * Draw electric arcs between particles
   */
  private updateArcs(): void {
    const { color } = this.options;

    this.arcs.forEach((arc) => {
      arc.clear();

      // Randomly show/hide arcs - show more often
      if (Math.random() > 0.7) {
        arc.visible = false;
        return;
      }

      // Pick two particles
      const p1Index = Math.floor(Math.random() * this.particles.length);
      const p1 = this.particles[p1Index];

      // Find another particle (can be any distance for longer arcs)
      const offset = 2 + Math.floor(Math.random() * 6);
      const p2Index = (p1Index + offset) % this.particles.length;
      const p2 = this.particles[p2Index];

      // Draw jagged electric arc with multiple segments
      const startX = p1.graphics.x;
      const startY = p1.graphics.y;
      const endX = p2.graphics.x;
      const endY = p2.graphics.y;

      arc.moveTo(startX, startY);

      // Add 1-3 jagged middle points
      const segments = 1 + Math.floor(Math.random() * 3);
      for (let s = 1; s <= segments; s++) {
        const t = s / (segments + 1);
        const midX = startX + (endX - startX) * t;
        const midY = startY + (endY - startY) * t;

        // Perpendicular jag
        const dx = endX - startX;
        const dy = endY - startY;
        const perpX = -dy;
        const perpY = dx;
        const len = Math.hypot(perpX, perpY) || 1;
        const jag = (Math.random() - 0.5) * 8;

        arc.lineTo(midX + (perpX / len) * jag, midY + (perpY / len) * jag);
      }

      arc.lineTo(endX, endY);

      // Vary the arc appearance
      const isIntense = Math.random() > 0.7;
      arc.stroke({
        color: isIntense ? 0xffffff : color!,
        width: isIntense ? 1.5 + Math.random() : 0.5 + Math.random() * 0.5,
        alpha: isIntense ? 0.9 : 0.4 + Math.random() * 0.3,
      });

      arc.visible = true;
    });
  }

  /**
   * Update size when sprite changes
   */
  updateSprite(sprite: Sprite, size: number): void {
    this.options.sprite = sprite;
    this.options.size = size;
    this.baseRadius = size / 2 + 4;

    // Update particle base radii
    this.particles.forEach((particle) => {
      particle.baseRadius = particle.layer === 'inner' ? this.baseRadius - 2 : this.baseRadius + 3;
      particle.radius = particle.baseRadius;
    });

    // Redraw glow ring
    this.drawGlowRing();
  }

  /**
   * Set the aura color
   */
  setColor(color: number): void {
    this.options.color = color;

    // Redraw particles with new color
    this.particles.forEach((particle) => {
      particle.graphics.clear();
      const particleColor = particle.layer === 'inner' ? 0xffffff : color;
      this.drawSparkle(particle.graphics, particleColor, particle.size);
    });

    // Redraw glow ring
    this.drawGlowRing();
  }

  dispose(): void {
    this.stopAnimation();
    this.particles = [];
    this.arcs = [];
    this.destroy({ children: true });
  }
}
